import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Trash2, 
  DollarSign,
  Car,
  CheckCircle,
  Clock,
  Loader2,
  Mail,
  Phone,
  User,
  Eye,
  EyeOff
} from 'lucide-react';

const AdminConciergeManagement = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [concierges, setConcierges] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: '', name: '' });
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });

  useEffect(() => {
    fetchConcierges();
  }, []);

  const fetchConcierges = async () => {
    try {
      const response = await axios.get(`${API}/admin/concierges`);
      setConcierges(response.data);
    } catch (error) {
      toast.error('Failed to load concierges');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConcierge = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/admin/concierges`, formData);
      toast.success('Concierge account created successfully');
      setShowCreateDialog(false);
      setFormData({ name: '', email: '', password: '', phone: '' });
      fetchConcierges();
    } catch (error) {
      toast.error('Failed to create concierge: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteConcierge = async () => {
    try {
      await axios.delete(`${API}/admin/concierges/${deleteDialog.id}`);
      toast.success('Concierge deleted');
      setDeleteDialog({ open: false, id: '', name: '' });
      fetchConcierges();
    } catch (error) {
      toast.error('Failed to delete concierge');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-concierge-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Concierge Management</h1>
          <p className="text-gray-500 mt-1">Manage concierge accounts and view their performance</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-concierge-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Concierge
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{concierges.length}</p>
                <p className="text-xs text-gray-500">Total Concierges</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Car className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">
                  {concierges.reduce((sum, c) => sum + (c.total_rides_posted || 0), 0)}
                </p>
                <p className="text-xs text-gray-500">Total Rides Posted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">
                  {concierges.reduce((sum, c) => sum + (c.completed_rides || 0), 0)}
                </p>
                <p className="text-xs text-gray-500">Completed Rides</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(concierges.reduce((sum, c) => sum + (c.total_commission_earned || 0), 0))}
                </p>
                <p className="text-xs text-gray-500">Total Commissions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Concierges List */}
      {concierges.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No concierges added yet</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Concierge
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {concierges.map(concierge => (
            <Card key={concierge.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{concierge.name}</CardTitle>
                      <Badge variant="outline" className="mt-1">Concierge</Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => setDeleteDialog({ open: true, id: concierge.id, name: concierge.name })}
                    data-testid={`delete-concierge-${concierge.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Mail className="h-4 w-4" />
                    <span>{concierge.email}</span>
                  </div>
                  {concierge.phone && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Phone className="h-4 w-4" />
                      <span>{concierge.phone}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{concierge.total_rides_posted || 0}</p>
                    <p className="text-xs text-gray-500">Rides Posted</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{concierge.completed_rides || 0}</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Total Earned</span>
                    <span className="text-lg font-bold text-purple-600">
                      {formatCurrency(concierge.total_commission_earned)}
                    </span>
                  </div>
                  {concierge.pending_commission > 0 && (
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-400">Pending</span>
                      <span className="text-sm text-gold-600">
                        {formatCurrency(concierge.pending_commission)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Concierge Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Concierge Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="John Smith"
                data-testid="concierge-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                placeholder="concierge@onyxreserve.net"
                data-testid="concierge-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={e => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  data-testid="concierge-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                placeholder="(305) 555-0123"
                data-testid="concierge-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateConcierge} data-testid="save-concierge-btn">
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({...deleteDialog, open})}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Concierge Account?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.name}"? This will remove their account but their historical ride data will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDeleteConcierge}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminConciergeManagement;
